import React, { useEffect } from 'react';
import { da, fi, sv, nb, nl, enGB } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import { addDays, startOfDay } from 'date-fns';
import { LANGUAGES_CODE } from 'utils/constants';

// ─── Locale helpers ───────────────────────────────────────────────────────────

// Aliases mirror utils/i18n/moment.js so locale resolution is consistent
// across calendar components (navigator/grid panels).
const LOCALE_MAP: Record<string, Locale> = {
    da,
    fi,
    sv,
    se: sv,
    nb,
    no: nb,
    nl,
};

export const getDateFnsLocale = (langCode: string): Locale => {
    const base = LOCALE_MAP[langCode] ?? enGB;
    return { ...base, options: { ...base.options, weekStartsOn: 1 as const } };
};

// ─── Date helpers ─────────────────────────────────────────────────────────────

export const buildRange = (start: Date, end: Date) => ({ startDate: start, endDate: end, key: 'date' });

export const formatDate = (date: Date, langCode: string): string =>
    date.toLocaleDateString(LANGUAGES_CODE[langCode] ?? 'en-GB');

/** Compare two dates by year+month only */
export const sameOrAfter = (a: Date, b: Date) =>
    a.getFullYear() > b.getFullYear() ||
    (a.getFullYear() === b.getFullYear() && a.getMonth() >= b.getMonth());

/** Same calendar month (and year)? */
export const sameMonth = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

/** Same day, ignoring the time of day. */
export const sameDay = (a: Date, b: Date) =>
    sameMonth(a, b) && a.getDate() === b.getDate();

// ─── Typed date input (dd-mm-yyyy) ────────────────────────────────────────────
//
// The calendar field can be typed into. Only the dd-mm-yyyy mask is supported for
// now, so it is deliberately not locale dependent. The field stores nothing but
// the digits the user has entered; separators, the range arrow and the untouched
// tail of the mask are all derived from them.

export const DATE_MASK = 'dd-mm-yyyy';
/** Drawn between the two dates of a range field. */
export const RANGE_SEPARATOR = ' -> ';
/** Digits a single masked date holds: dd + mm + yyyy. */
export const DIGITS_PER_DATE = 8;

export const maskTemplate = (isRange: boolean): string =>
    isRange ? `${DATE_MASK}${RANGE_SEPARATOR}${DATE_MASK}` : DATE_MASK;

/** How many digits the field accepts in total. */
export const maxMaskDigits = (isRange: boolean): number =>
    isRange ? DIGITS_PER_DATE * 2 : DIGITS_PER_DATE;

/** Reduce anything typed or pasted into the field to the digits we store. */
export const extractDigits = (value: string): string => value.replace(/\D/g, '');

const pad = (value: number, length: number) => String(value).padStart(length, '0');

/** A date as the 'ddmmyyyy' digit string the masked field stores. */
export const dateToDigits = (date: Date): string =>
    `${pad(date.getDate(), 2)}${pad(date.getMonth() + 1, 2)}${pad(date.getFullYear(), 4)}`;

const isDigitSlot = (char: string) => char === 'd' || char === 'm' || char === 'y';

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * Split the field into the part the user has committed (drawn as normal input
 * text) and the part still to be filled in (drawn as ghost text).
 *
 * `completions` holds one 'ddmmyyyy' string per date of the mask — the value the
 * ghost suggests — or null to fall back to the mask letters themselves.
 *
 * Everything up to and including the last typed digit counts as typed, so a
 * separator only turns solid once the user has passed it:
 *   digits "03" -> typed "03", ghost "-08-2026 -> 11-08-2026"
 */
export const buildMaskedValue = (
    digits: string,
    completions: Array<string | null>,
    isRange: boolean,
): { typed: string; ghost: string } => {
    let display = '';
    let typedLength = 0;
    let slot = 0;

    for (const char of maskTemplate(isRange)) {
        if (!isDigitSlot(char)) {
            display += char;
            continue;
        }
        const completion = completions[Math.floor(slot / DIGITS_PER_DATE)];
        display += slot < digits.length
            ? digits[slot]
            : (completion?.[slot % DIGITS_PER_DATE] ?? char);
        slot += 1;
        if (slot === digits.length) typedLength = display.length;
    }

    return { typed: display.slice(0, typedLength), ghost: display.slice(typedLength) };
};

/**
 * The 'ddmmyyyy' digits the ghost text should suggest for one date of the mask,
 * or null when there is nothing to suggest. A suggestion has to keep what the
 * user typed as its prefix, otherwise the ghost would contradict the typed text.
 */
export const completionDigits = (candidate: Date | null, digits: string): string | null => {
    if (!candidate) return null;
    const full = dateToDigits(candidate);
    return full.startsWith(digits) ? full : null;
};

/**
 * ~12 years of days. Long enough to reach any dd, dd-mm or dd-mm-yy prefix;
 * prefixes that pin a year further out are handled by digitsToDate instead.
 */
const MAX_SCAN_DAYS = 366 * 12;

const daysInMonth = (year: number, month: number) => new Date(year, month, 0).getDate();

/**
 * The days of `year`/`month` (1-based) whose number can still match the typed day
 * digits, latest first. Two digits pin the day outright; one digit is its tens.
 */
const candidateDays = (digits: string, year: number, month: number): number[] => {
    const last = daysInMonth(year, month);
    if (digits.length >= 2) {
        const day = Number(digits.slice(0, 2));
        return day >= 1 && day <= last ? [day] : [];
    }
    const tens = Number(digits[0]) * 10;
    const days: number[] = [];
    for (let day = Math.min(tens + 9, last); day >= Math.max(tens, 1); day -= 1) days.push(day);
    return days;
};

/** Could any day of this month hold a match for the typed month and year digits? */
const monthCanMatch = (digits: string, year: number, month: number): boolean =>
    (digits.length < 3 || pad(month, 2).startsWith(digits.slice(2, 4)))
    && (digits.length < 5 || pad(year, 4).startsWith(digits.slice(4)));

/**
 * The date nearest `start` within the inclusive `min`..`max` window whose digits
 * begin with `digits`, searching in `step` direction.
 *
 * Only the days that could possibly match are looked at, a month at a time, so a
 * fully typed date is reached straight away instead of walking every day in
 * between — the field is re-resolved on each keystroke, so this stays off the
 * critical path even on a slow device.
 */
const scanForMatch = (digits: string, start: Date, step: -1 | 1, min: Date, max: Date): Date | null => {
    if (min > max) return null;

    // Only the far end of the window bounds the walk. The near end is checked per
    // candidate instead, because it often falls inside the month the walk starts
    // from — going forward from the 28th, `min` is already in the next month.
    const far = step < 0 ? min : max;
    const stopMonths = far.getFullYear() * 12 + far.getMonth();

    for (
        let months = start.getFullYear() * 12 + start.getMonth();
        step < 0 ? months >= stopMonths : months <= stopMonths;
        months += step
    ) {
        const year = Math.floor(months / 12);
        const month = (months % 12) + 1;
        if (!monthCanMatch(digits, year, month)) continue;

        const days = candidateDays(digits, year, month);
        // Nearest first: the latest day of the month going back, the earliest going on.
        if (step > 0) days.reverse();
        for (const day of days) {
            const candidate = new Date(year, month - 1, day);
            if (candidate < min || candidate > max) continue;
            if (dateToDigits(candidate).startsWith(digits)) return candidate;
        }
    }
    return null;
};

/**
 * Read the typed digits at face value, borrowing the reference date's digits for
 * the positions the user has not reached yet. Keeps `digits` as a prefix, so the
 * result can still be completed by ghost text.
 */
const digitsToDate = (digits: string, reference: Date): Date => {
    const padded = (digits + dateToDigits(reference).slice(digits.length)).slice(0, DIGITS_PER_DATE);
    const month = clamp(Number(padded.slice(2, 4)), 1, 12);
    const year = Number(padded.slice(4));
    const day = clamp(Number(padded.slice(0, 2)), 1, new Date(year, month, 0).getDate());
    return new Date(year, month - 1, day);
};

/**
 * The date a partially typed dd-mm-yyyy value points at, or null when the digits
 * cannot spell one (e.g. a day of "99").
 *
 * Resolution walks back in time from `reference`: typing "03" on 11-08-2026 means
 * the 3rd of August, not of September. Fields that cannot go back (those with a
 * `minDate`, e.g. the postpone picker) walk forward from their floor instead, and
 * never resolve to a date before it — a fully typed date below the floor is no
 * date at all, so the field falls back to its last valid value.
 * The returned date always keeps `digits` as its 'ddmmyyyy' prefix.
 */
export const resolveTypedDate = (digits: string, reference: Date, minDate?: Date): Date | null => {
    if (!digits) return null;
    // A day the longest month cannot hold ("99", "4") exists nowhere, so there is
    // nothing to scan for: digitsToDate would only clamp it into a date that no
    // longer starts with what was typed.
    if (!candidateDays(digits, 2024, 1).length) return null;

    const floor = minDate ? startOfDay(minDate) : null;
    const anchor = startOfDay(floor && floor > reference ? floor : reference);

    // Back from the anchor first, then on from it, each within the scan window and
    // never below the floor.
    const earliest = addDays(anchor, -MAX_SCAN_DAYS);
    const back = scanForMatch(digits, anchor, -1, floor && floor > earliest ? floor : earliest, anchor);
    if (back) return back;

    const forward = scanForMatch(digits, anchor, 1, addDays(anchor, 1), addDays(anchor, MAX_SCAN_DAYS + 1));
    if (forward) return forward;

    const direct = digitsToDate(digits, anchor);
    if (!dateToDigits(direct).startsWith(digits)) return null;
    return floor && direct < floor ? null : direct;
};

// ─── Keyboard focus ───────────────────────────────────────────────────────────
//
// The popup and the month/year grids are portaled to <body>, so they sit after
// everything else in the document. Tab order would send the user past the whole
// page instead of into the calendar, so focus is routed by hand.

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]',
].join(',');

/** Everything inside `container` the user can Tab to, in document order. */
export const focusableWithin = (container: HTMLElement | null): HTMLElement[] => {
    if (!container) return [];
    return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        .filter(element => element.tabIndex >= 0 && !element.hasAttribute('disabled'));
};

/**
 * Move focus where Tab would have taken it after `anchor`, had the portaled parts
 * of the calendar not been in the way — i.e. on to the next control further down
 * the page. Returns whether anything took the focus.
 */
export const focusNextTabbable = (
    anchor: HTMLElement | null,
    excluded: Array<HTMLElement | null>,
): boolean => {
    if (!anchor) return false;
    const skip = [anchor, ...excluded];
    const candidates = focusableWithin(document.body).filter(element =>
        !skip.some(part => part?.contains(element))
        && !!(anchor.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING),
    );
    // A candidate can be present but unfocusable (inside a collapsed menu, say), and
    // focus() is then a no-op — so keep going until one actually takes it.
    return candidates.some(element => {
        element.focus();
        return document.activeElement === element;
    });
};

/** Keep Tab inside `container`, wrapping around at both ends. */
export const trapTabKey = (event: React.KeyboardEvent, container: HTMLElement | null): void => {
    if (event.key !== 'Tab') return;
    const items = focusableWithin(container);
    if (!items.length) return;

    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
    }
};

// ─── Hook: close a popover when clicking outside it ──────────────────────────

export const usePopoverClose = (
    isOpen: boolean,
    ref: React.RefObject<HTMLDivElement>,
    onClose: () => void,
) => {
    useEffect(() => {
        if (!isOpen) return;
        let cleanup: (() => void) | null = null;
        const timer = setTimeout(() => {
            const handler = (e: MouseEvent) => {
                if (ref.current && !ref.current.contains(e.target as Node)) onClose();
            };
            document.addEventListener('mousedown', handler);
            cleanup = () => document.removeEventListener('mousedown', handler);
        }, 50);
        return () => { clearTimeout(timer); cleanup?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);
};
